import React, { useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { overrideProperties } from '@ecp/common/src/style/utils';
import { sizePropTypes } from '@ecp/common/src/style/propTypes';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import Button from '@ecp/common/src/components/button/bo/Button';
import Pagination from '@ecp/common/src/components/pagination/bo/Pagination';

import {
  gridPropTypes,
  handleGridFunctionPropTypes,
  IBSheetEventPropTypes,
  leftHeaderButtonPropTypes,
  pagingPropTypes,
  rightHeaderButtonPropTypes,
} from '@bo-components/common/grid/gridPropTypes';
import useGrid from '@bo-components/common/grid/useGrid';
import GridHeader from '@bo-components/common/grid/GridHeader';

const GridContainer = React.forwardRef(
  (
    {
      height,
      showDelete,
      showCopy,
      excelTooltip,
      excel,
      onRegister,
      showAddRow,
      showRegister,
      addRowLabel,
      numberOfPageGroup,
      showFocusedBackground,
      hidePageSize,
      hideHeader,
      hideFooter,
      gap = '10px',
      totalUnit,
      headerButtonEnabled,
      children,
      showChangeRow,
      changeRowLabel,
      changeRowOnClick,
      ...props
    },
    ref
  ) => {
    const {
      grid,
      sheetEl,
      unPaged,
      total,
      isHeaderEnabled,
      checkedRows,
      onSave,
      getPageSize,
      getPageIndex,
      getPageSort,
      setPageIndex,
      setPageSort,
      setPageSize,
      handleSizeChange,
      handlePageChange,
      handleDeleteRows,
      handleCopyRows,
      handleAddRow,
      handleSave,
      handleExcelTemplateDownload,
      handleExcelDownload,
      handleExcelUpload,
      handleExcelUploadWithMaxFileSize,
      validate,
      getChangedData,
      reloadData,
      getCheckedRows,
      setFocusOut,
      cancelDeletedRow,
      cancelDeletedRows,
    } = useGrid({ ...props }, ref);

    useImperativeHandle(ref, () => ({
      hasChangedData() {
        return grid.current?.hasChangedData();
      },
      getDataRows() {
        return grid?.current?.getDataRows();
      },
      getPageSize() {
        return getPageSize().key;
      },
      getPageSort() {
        return getPageSort();
      },
      getPageIndex() {
        return getPageIndex();
      },
      setFocusOut() {
        return setFocusOut();
      },
      setPageSort(sort, ignoreEvent = true) {
        setPageSort(sort);

        const doSort = (sort) => (sheet) => {
          if (!sort?.includes?.(',')) {
            ignoreEvent ? (sheet.Sort = sort) : sheet.doSort(sort);
          } else {
            const [key, order] = sort.split(',');
            const sortName = sheet.getCols('Name', 'Key').find((name) => {
              return key === sheet.getAttribute(null, name, 'Key');
            });

            const sortDetail = `${order === 'desc' ? '-' : ''}${sortName || key}`;
            ignoreEvent ? (sheet.Sort = sortDetail) : sheet.doSort(sortDetail);
          }
        };

        if (grid?.current?.doSort) {
          doSort(sort)(grid.current);
        } else {
          props.renderSafeJobs().push(doSort(sort));
        }
      },
      setData(data = []) {
        const loadSearchData =
          (data = []) =>
          (sheet) => {
            sheet.loadSearchData(data);
          };

        if (grid?.current?.loadSearchData) {
          loadSearchData(data)(grid.current);
        } else {
          props.renderSafeJobs().push(loadSearchData(data));
        }
      },
      setPageIndex(index) {
        setPageIndex(index);
      },
      setPageSize(size) {
        setPageSize(size);
      },
      setCellValidationErrorStyle(rowId, colId, message) {
        grid?.current?.setCellStyle({
          row: grid.current.getRowById(rowId),
          col: colId,
          styleAttr: { Error: true, Tip: message },
          render: 1,
        });
      },
      setRowValidationErrorStyle(rowId, colId, message) {
        const row = grid?.current?.getRowById(rowId);
        grid?.current?.setCellStyle({
          row: row,
          col: colId,
          styleAttr: { Error: true, Tip: message },
          render: 1,
        });
        grid?.current?.setValue?.(row, 'ErrorDescription', message);
      },
      clearSort() {
        setPageSort('');
        if (grid?.current?.Sort) {
          grid.current.Sort = '';
        }
      },
      async validate() {
        return await validate();
      },
      async reloadData() {
        return await reloadData();
      },
      async handleSave() {
        await handleSave();
      },
      getChangedData() {
        return getChangedData();
      },
      checkedRows() {
        return getCheckedRows();
      },
      focusByRowIndex(rowIndex, colId, ignoreEvent = false, wait = 1000) {
        setTimeout(() => {
          const row = grid?.current?.getRowByIndex(rowIndex);
          row && grid?.current?.focus(row, colId, null, ignoreEvent);
        }, wait);
      },
      focusByRowId(rowId, colId, ignoreEvent = false, wait = 1000) {
        setTimeout(() => {
          const row = grid?.current?.getRowById(rowId);
          row && grid?.current?.focus(row, colId, null, ignoreEvent);
        }, wait);
      },
      setFocusedRowValues(values = [], render = true, ignoreEvent = false) {
        const row = grid?.current?.getFocusedRow();
        row &&
          values.forEach(({ name, value, ...attributes }) => {
            grid?.current?.setValue(row, name, value, render, ignoreEvent);

            Object.keys(attributes).forEach((attribute) => {
              grid?.current?.setAttribute(row, name, attribute, attributes[attribute], render);
            });
          });
      },
      setValue(row, col, value, render = true, ignoreEvent = false) {
        grid?.current?.setValue(row, col, value, render, ignoreEvent);
      },
      setRowsValue(rows = [], col, value, render = true, ignoreEvent = false) {
        rows.forEach((row) => {
          grid?.current?.setValue(row, col, value, render, ignoreEvent);
        });
      },
      setAttribute(row, col, attr, val, render) {
        grid?.current?.setAttribute(row, col, attr, val, render);
      },
      showTreeLevel(level) {
        grid?.current?.showTreeLevel(level);
      },
      cancelDeletedRow(row) {
        cancelDeletedRow(row);
      },
      cancelDeletedRows(rows) {
        cancelDeletedRows(rows);
      },
      deleteFocusedRow() {
        const focusedRow = grid?.current?.getFocusedRow();
        grid?.current?.removeRows([focusedRow]);
      },
      addRowAndFocus() {
        grid?.current?.addRow({ next: grid.current.getFirstRow?.() });
      },
      getRowValue(row) {
        return grid?.current?.getRowValue(row);
      },
      removeRow(row) {
        grid?.current?.removeRow(row);
      },
      acceptChangedData() {
        grid?.current?.acceptChangedData();
      },
      removeAll() {
        grid?.current?.removeAll();
      }
    }));

    return (
      <FlexBoxColumn width={'100%'} align-items={'flex-start'} gap={gap}>
        {!hideHeader && (
          <GridHeader
            total={total}
            unPaged={unPaged}
            grid={grid}
            hidePageSize={hidePageSize}
            pageSize={getPageSize()}
            onSizeChange={handleSizeChange}
            showDelete={showDelete}
            headerButtonEnabled={headerButtonEnabled}
            isHeaderEnabled={isHeaderEnabled}
            onDeleteRows={handleDeleteRows}
            showCopy={showCopy}
            onCopyRows={handleCopyRows}
            checkedRows={checkedRows}
            excelTooltip={excelTooltip}
            excel={excel}
            onExcelDownload={handleExcelDownload}
            onExcelTemplateDownload={handleExcelTemplateDownload}
            onExcelUpload={handleExcelUpload}
            onExcelUploadWithMaxFileSize={handleExcelUploadWithMaxFileSize}
            onAddRow={handleAddRow}
            onRegister={onRegister}
            showAddRow={showAddRow}
            addRowLabel={addRowLabel}
            showRegister={showRegister}
            totalUnit={totalUnit}
            customExcelForm={props.customExcelForm}
            sizeEnl={props?.sizeEnl}
            gridSizes={props?.gridSizes} // ECMJ - 박준석
            showChangeRow={showChangeRow}
            changeRowLabel={changeRowLabel}
            changeRowOnClick={changeRowOnClick}
          >
            {children}
          </GridHeader>
        )}
        <GridWrapper height={height} showFocusedBackground={showFocusedBackground}>
          <div id={sheetEl.current} style={{ width: '100%', height: '100%' }} />
        </GridWrapper>
        {!hideFooter && (
          <FlexBox width={'100%'} justify-content={onSave ? 'space-between' : 'center'}>
            {onSave && <Spacing left={'90px'} />}
            {!unPaged && (
              <Pagination
                totalCount={total}
                page={getPageIndex() + 1}
                countPerPage={getPageSize().key}
                onPageChange={handlePageChange}
                numberOfPageGroup={numberOfPageGroup}
              />
            )}
            {onSave && (
              <Button type="Primary" width={props.saveButtonWidth || '90px'} onClick={handleSave}>
                {props.saveButtonLabel || '저장'}
              </Button>
            )}
          </FlexBox>
        )}
      </FlexBoxColumn>
    );
  }
);

GridContainer.propTypes = {
  ...gridPropTypes,
  /* UploadPopup custom props */
  showUploadPopup: PropTypes.bool,
  /* LeftHeaderButtons */
  ...leftHeaderButtonPropTypes,
  /* RightHeaderButtons */
  ...rightHeaderButtonPropTypes,
  /* Paging */
  ...pagingPropTypes,
  /* Handle Functions */
  ...handleGridFunctionPropTypes,
  /* IBSheet Event */
  ...IBSheetEventPropTypes,
};

GridContainer.displayName = 'GridBody';

export default GridContainer;

const roundCss = css`
  display: flex;
  width: 90px;
  height: 24px;
  border-radius: 12px;
  box-sizing: border-box;
  border: 1px solid #cccccc;
  background: #eeeeee;
  color: #333333;
  justify-content: center;
  align-items: center;
  margin: auto;
`;
export const GridWrapper = styled.div`
  ${overrideProperties(sizePropTypes, {
    width: '100%',
    height: '275px',
  })};
  min-height: 89px;

  .IBColorHoveredCell {
    //:: TODO
    background-color: ${({ showFocusedBackground }) =>
      !showFocusedBackground ? 'transparent' : 'rgba(0, 0, 0, 0.04)'};
  }

  .grid {
    &__checkbox > div {
      background-position: center;
      cursor: pointer;
    }

    &__drag-and-drop {
      background-size: 16px !important;
      background-position: center !important;
    }

    &__image-link {
      background-size: 28px !important;
      background-position: center !important;
    }

    &__round-item {
      ${roundCss};

      &-wrapper {
        display: flex;
        gap: 4px;
        justify-content: center;
        align-items: center;
        margin: auto;
        width: max-content;
      }

      &__blue {
        ${roundCss};
        background: #e9e8fb;
        border: 1px solid #dbd8ff;
        color: #6c60ff;
      }

      &__red {
        ${roundCss};
        background: #fce7e7;
        border: 1px solid #edbcbc;
        color: #e10808;
      }

      &__grey {
        ${roundCss};
        background: #cccccc;
        border: 1px solid rgba(0, 0, 0, 0.2);
        color: #333333;
      }

      &__green {
        ${roundCss};
        background: #17864c;
        border: 1px solid #17864c33;
        color: #fff;
      }
      &__border-green {
        ${roundCss};
        background: #fff;
        border: 1px solid #17864c;
        color: #17864c;
      }
      &__darkgray {
        ${roundCss};
        background: #cccccc;
        border: 1px solid rgba(39, 39, 39, 0.2);
        color: #393939;
      }
    }

    &__icon-text {
      &.IBText.IBIconLeft.IBEmpty {
        background-image: none !important;
        cursor: initial;
      }
    }
  }
`;
